import { AlertColor } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { getUserOrg } from '../../AddUser/userService';
import { AddEditPaymentConfigForm } from './AddEditPaymentConfiguration/AddEditPaymentConfigurationForm';

const emptyFormValues: FormDataModel = {
  organizationId: '',
  paymentProvider: '',
  merchantId: '',
  merchantUserName: '',
  merchantPassword: '',
  merchantSecret: '',
  defaultPaymentGateway: false,
  transactionWallet: '',
  transactionCurrency: '',
  transactionIsoCurrency: '',
  redirectUrl: '',
  cancelUrl: '',
  subAccountId: '',
};

const PaymentConfigurationList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];

  const [organizations, setOrganizations] = useState<{ label: string; value: number }[]>([]);
  const [paymentProviders, setPaymentProviders] = useState<{ label: string; value: string }[]>([]);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    return AddEditPaymentConfigForm(strings, organizations as any, paymentProviders as any).map(field => ({
      ...field,
      responsive: field.type === 'checkbox' ? { xs: 12 } : { xs: 12, sm: 6 },
    }));
  }, [organizations, paymentProviders]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.paymentConfig, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    (async () => {
      try {
        const [orgResponse, { data: providers }] = await Promise.all([
          getUserOrg(),
          axiosInstance.get(URLS.paymentProviderDropdowns),
        ]);
        setOrganizations((orgResponse || []).map((item: any) => ({ label: item.name, value: item.id })));
        setPaymentProviders((providers || []).map((item: any) => ({ label: item.name, value: item.id })));
      } catch {
        // ignore dropdown load failures
      }
    })();
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(async (recordId: number | null) => {
    setEditingId(recordId);
    if (recordId) {
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.paymentConfigById(recordId));
        setFormValues({
          organizationId: data?.organization?.value ?? '',
          paymentProvider: data?.paymentProvider?.value ?? '',
          merchantId: data?.merchantId ?? '',
          merchantUserName: data?.merchantUserName ?? '',
          merchantPassword: data?.merchantPassword ?? '',
          merchantSecret: data?.merchantSecret ?? '',
          defaultPaymentGateway: data?.defaultPaymentGateway ?? false,
          transactionWallet: data?.transactionWallet ?? '',
          transactionCurrency: data?.transactionCurrency ?? '',
          transactionIsoCurrency: data?.transactionIsoCurrency ?? '',
          redirectUrl: data?.redirectUrl ?? '',
          cancelUrl: data?.cancelUrl ?? '',
          subAccountId: data?.subAccountId ?? '',
        });
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
    } else {
      setFormValues({ ...emptyFormValues });
    }
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = {
        organizationId: body.organizationId,
        paymentProvider: body.paymentProvider,
        merchantId: body.merchantId,
        merchantUserName: body.merchantUserName,
        merchantPassword: body.merchantPassword,
        merchantSecret: body.merchantSecret,
        defaultPaymentGateway: Boolean(body.defaultPaymentGateway),
        transactionWallet: body.transactionWallet,
        transactionCurrency: body.transactionCurrency,
        transactionIsoCurrency: body.transactionIsoCurrency,
        redirectUrl: body.redirectUrl,
        cancelUrl: body.cancelUrl,
        subAccountId: body.subAccountId,
      };
      if (editingId) {
        await axiosInstance.put(URLS.paymentConfigById(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addPaymentConfig, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.paymentConfigById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.menu_text_payment_config,
      subtitle: strings.payment_config_list_subtitle,
      searchField: 'organizationName',
      searchPlaceholder: 'Search payment configurations',
      AddButtonText: strings.payment_config_title_add,
      listApi: URLS.paymentConfig,
      deleteApi: URLS.paymentConfigById,
      addPrivilege: privileges.add_payment_config,
      editPrivilege: privileges.edit_payment_config,
      deletePrivilege: privileges.delete_payment_config,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        { field: 'organization', headerName: strings.payment_config_column_hotel_Name, flex: 3, sortable: true },
        {
          field: 'paymentProvider',
          headerName: strings.payment_config_column_payment_provider,
          flex: 2,
          sortable: true,
        },
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.payment_config_title_edit : strings.payment_config_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_payment_config)}
        width="60%"
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default PaymentConfigurationList;
